import Cookies from "js-cookie";

// --- Type Definitions ---
export interface Permission {
  module_id: number;
  module_name: string;
  access_level: PermissionAction;
}

export type PermissionAction =
  | "view"
  | "edit"
  | "create"
  | "delete"
  | "assign"
  | "merge";

// --- Route to Module Mapping ---
const pathMapping: Record<string, number> = {
  "/dashboard": 1,
  "/tickets": 2,
  "/members": 3,
  "/unit-groups": 4,
  "/roles": 5,
};

// --- Normalize flat cookie permission modules ---
export interface FlatPermissionModule {
  module_id: number;
  module_name: string;
  [key: string]: boolean | number | string;
}

export const normalizePermissions = (
  modules: FlatPermissionModule[]
): Permission[] => {
  const result: Permission[] = [];

  modules.forEach((mod) => {
    Object.entries(mod).forEach(([key, value]) => {
      if (
        ["view", "edit", "create", "delete", "assign", "merge"].includes(key) &&
        value === true
      ) {
        result.push({
          module_id: mod.module_id,
          module_name: mod.module_name,
          access_level: key as PermissionAction,
        });
      }
    });
  });

  return result;
};

// --- Retrieve permissions from cookies ---
export const userPermissions: Permission[] = (() => {
  const raw = Cookies.get("userPermissions");
  if (!raw) {
    console.warn("⚠️ No userPermissions cookie found.");
    return [];
  }

  try {
    const modules = JSON.parse(raw) as FlatPermissionModule[];
    console.group("📦 Raw Cookie Data (userPermissions)");
    console.dir(modules, { depth: null });
    console.groupEnd();

    const normalized = normalizePermissions(modules);
    console.group("✅ Normalized Permissions");
    console.table(
      normalized.map((p) => ({
        ModuleID: p.module_id,
        ModuleName: p.module_name,
        Action: p.access_level,
      }))
    );
    console.groupEnd();

    return normalized;
  } catch (err) {
    console.error("❌ Failed to parse userPermissions from cookies:", err);
    return [];
  }
})();

// --- Check if user has permission for a module/action ---
export const isPermit = (
  moduleId: number,
  action: PermissionAction
): boolean => {
  return userPermissions.some(
    (p) => p.module_id === moduleId && p.access_level === action
  );
};

// --- Check if user has permission for a route ---
export const hasRoutePermission = (
  currentPath: string,
  action: PermissionAction = "view"
): boolean => {
  const matchedPath = Object.keys(pathMapping).find((route) =>
    currentPath.startsWith(route)
  );
  if (!matchedPath) return false;

  const moduleId = pathMapping[matchedPath];
  return isPermit(moduleId, action);
};

// --- 🔍 Debugging: Log permissions for each module ---
Object.entries(pathMapping).forEach(([path, moduleId]) => {
  const perms = userPermissions.filter((p) => p.module_id === moduleId);
  if (perms.length) {
    console.table(
      perms.map((p) => ({
        Module: p.module_name,
        Action: p.access_level,
      }))
    );
  } else {
  }
  console.groupEnd();
});
console.groupEnd();
